import React, { useCallback, useEffect } from "react";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import MobileStickyBar from "@/components/layout/MobileStickyBar";
import LanguageBanner from "@/components/layout/LanguageBanner";
import { useLang } from "@/hooks/useLang";
import { trackA11yEvent } from "@/content/site";

export default function Layout({ children }) {
  const lang = useLang();

  // [Phase 24, 2026-08-26] Accessibility analytics — see
  // ``trackA11yEvent`` for the full rationale. This effect fires ONCE
  // per session on mount to detect whether the visitor has
  // ``prefers-reduced-motion: reduce`` set at the OS level. Strong
  // proxy for visitors relying on assistive tech (vestibular, autism-
  // spectrum, low-vision). The event is session-scoped and PII-free.
  useEffect(() => {
    try {
      if (typeof window === "undefined" || !window.matchMedia) return;
      const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
      if (mq && mq.matches) {
        trackA11yEvent("a11y_prefers_reduced_motion", {
          setting: "reduce",
          page: window.location.pathname,
        });
      }
    } catch (_) { /* non-fatal */ }
  }, []);

  const handleSkipFocus = useCallback(() => {
    // Fires when the skip link receives keyboard focus (i.e. the user
    // pressed Tab on page load). Session-scoped so it fires at most
    // once per visit — the goal is "how many visitors navigate by
    // keyboard," not "how many times they tabbed."
    trackA11yEvent("a11y_skip_link_shown", {
      lang,
      page: typeof window !== "undefined" ? window.location.pathname : "",
    });
  }, [lang]);

  const handleSkipActivate = useCallback(() => {
    // Fires on actual activation — click, Enter, Space. Even stronger
    // proxy for keyboard/AT reliance because the user affirmatively
    // took the bypass block. Session-scoped.
    trackA11yEvent("a11y_skip_link_used", {
      lang,
      page: typeof window !== "undefined" ? window.location.pathname : "",
    });
  }, [lang]);

  return (
    <>
      <a
        href="#main"
        className="gw-skip-link"
        data-testid="skip-to-content-link"
        onFocus={handleSkipFocus}
        onClick={handleSkipActivate}
      >
        {lang === "es" ? "Ir al contenido principal" : "Skip to main content"}
      </a>
      <LanguageBanner />
      <Header />
      <main id="main" tabIndex="-1" aria-label={lang === "es" ? "Contenido principal" : "Main content"}>
        {children}
      </main>
      <Footer />
      <MobileStickyBar />
    </>
  );
}
